import type {
  ClaimResponse,
  ClaimStrategyResponse,
} from "@patent-studio/api-types";
import { AddButton } from "@/components/ui/AddButton";
import { Button } from "@/components/ui/button";
import { DisclosureToggle } from "@/components/ui/Disclosure";
import { plural } from "@/lib/plural";
import { Panel } from "@/components/ui/Panel";
import { toneClasses } from "@/components/ui/tone";
import { useSortableItem, useTarget } from "@/components/patterns/SortableList";
import { useAddClaim } from "@/hooks/use-claim-strategy";
import { mergeRefs } from "@/lib/merge-refs";
import { cn } from "@/lib/utils";
import { Refusal } from "./ChainControls";
import { ClaimRow } from "./ClaimRow";
import { useChangesOn } from "../notifications/StepChanges";
import { INDEPENDENTS, ontoClaim, underClaim } from "./claim-lanes";

type CardState = "open" | "confirmed" | "placeholder";

/** The claims beneath the one named, however deep, in the order the card draws them. */
function everyBeneath(
  claimId: string,
  childrenOf: (claimId: string) => ClaimResponse[],
): ClaimResponse[] {
  return childrenOf(claimId).flatMap((child) => [
    child,
    ...everyBeneath(child.claim_id, childrenOf),
  ]);
}

/**
 * Placeholder over everything; confirmed once the claim and every claim beneath
 * it are settled. A placeholder dependent is settled, as the lock gate reads it:
 * waiting for it to be confirmed held the card open for good.
 */
function stateOf(claim: ClaimResponse, dependents: ClaimResponse[]): CardState {
  if (claim.placeholder) return "placeholder";
  return claim.confirmed &&
    dependents.every((d) => d.confirmed || d.placeholder)
    ? "confirmed"
    : "open";
}

const CARD: Record<CardState, string> = {
  open: cn("border-border", toneClasses("warning", "edge")),
  confirmed: cn(
    "border-success-border ring-1 ring-success/30",
    toneClasses("success", "edge"),
  ),
  placeholder: cn(
    "border-provisional-border ring-1 ring-provisional/20 [border-left-style:dashed]",
    toneClasses("provisional", "edge"),
  ),
};

const BAND: Record<CardState, string> = {
  open: "bg-warning-surface",
  confirmed: "bg-success-surface",
  placeholder: "bg-provisional-surface",
};

/** A dependent's own mark: a ring when confirmed, a warm ground when a placeholder. */
function dependentState(claim: ClaimResponse): string {
  if (claim.placeholder) {
    return "rounded-inset bg-provisional-surface/60 ring-1 ring-provisional-border ring-inset";
  }
  return claim.confirmed
    ? "rounded-inset ring-1 ring-success-border ring-inset"
    : "";
}

/** The well that adds an empty dependent under the claim, and its refusal. */
function NewDependent({
  taskId,
  claim,
}: {
  taskId: string;
  claim: ClaimResponse;
}) {
  const add = useAddClaim(taskId);
  return (
    <>
      <AddButton
        className="mt-[9px]"
        disabled={add.isPending}
        onClick={() => {
          add.mutate({ parent_claim_id: claim.claim_id });
        }}
      >
        New dependent claim
      </AddButton>
      {add.isError && <Refusal error={add.error} />}
    </>
  );
}

/**
 * A dependent under its claim, sorting only among that claim's dependents, and
 * a place a position can be dropped to hang a fallback from it.
 */
function DependentRow({
  taskId,
  editable,
  stage,
  claim,
  parent,
  root,
  index,
  depth,
}: {
  taskId: string;
  editable: boolean;
  stage: ClaimStrategyResponse;
  claim: ClaimResponse;
  parent: ClaimResponse;
  root: ClaimResponse;
  index: number;
  /** How deep the row sits in the card, so a drop lands on the row the pointer is over. */
  depth: number;
}) {
  const row = useSortableItem({
    id: claim.claim_id,
    index,
    lane: underClaim(parent.claim_id),
    type: `dependent:${parent.claim_id}`,
    label: `Reorder claim ${String(claim.number)}`,
  });
  const target = useTarget({
    id: ontoClaim(claim.claim_id),
    accept: "position",
    inner: true,
    depth,
  });
  return (
    <ClaimRow
      taskId={taskId}
      editable={editable}
      stage={stage}
      claim={claim}
      rowRef={editable ? mergeRefs(row.ref, target.ref) : undefined}
      lead={editable ? row.handle : undefined}
      className={cn(
        "px-2 py-[9px]",
        // Only the first claim under the band draws no rule: the band's own edge is there.
        (parent !== root || index > 0) && "border-t border-border-subtle",
        dependentState(claim),
        row.chrome,
        target.chrome,
      )}
    />
  );
}

/**
 * The claims directly beneath a claim, each followed by its own, down a rule
 * that steps in once for every level, so a fallback reads under the claim it
 * narrows.
 */
function Beneath({
  taskId,
  editable,
  stage,
  parent,
  root,
  childrenOf,
  depth,
}: {
  taskId: string;
  editable: boolean;
  stage: ClaimStrategyResponse;
  parent: ClaimResponse;
  root: ClaimResponse;
  childrenOf: (claimId: string) => ClaimResponse[];
  /** How deep the claims drawn here sit in the card. */
  depth: number;
}) {
  const children = childrenOf(parent.claim_id);
  if (children.length === 0) return null;
  return (
    <div className="relative pl-[18px] before:absolute before:top-0 before:bottom-3 before:left-2 before:w-[1.5px] before:bg-border">
      {children.map((child, at) => (
        <div key={child.claim_id}>
          <DependentRow
            taskId={taskId}
            editable={editable}
            stage={stage}
            claim={child}
            parent={parent}
            root={root}
            index={at}
            depth={depth}
          />
          <Beneath
            taskId={taskId}
            editable={editable}
            stage={stage}
            parent={child}
            root={root}
            childrenOf={childrenOf}
            depth={depth + 1}
          />
        </div>
      ))}
    </div>
  );
}

/** Under the claim: its dependents and the well to add one, or, folded, how many are hidden. */
function CardBody({
  taskId,
  editable,
  stage,
  claim,
  childrenOf,
  hidden,
  onShow,
}: {
  taskId: string;
  editable: boolean;
  stage: ClaimStrategyResponse;
  claim: ClaimResponse;
  childrenOf: (claimId: string) => ClaimResponse[];
  /** How many claims are folded away beneath, or null while they are shown. */
  hidden: number | null;
  onShow: () => void;
}) {
  if (hidden !== null) {
    return (
      <div className="px-3.5 pb-3">
        <Button type="button" variant="link" size="dense" onClick={onShow}>
          {hidden === 0
            ? "No dependent claims · Show"
            : `${plural(hidden, "dependent claim")} hidden · Show`}
        </Button>
      </div>
    );
  }
  return (
    <div className="px-3.5 pb-3">
      <Beneath
        taskId={taskId}
        editable={editable}
        stage={stage}
        parent={claim}
        root={claim}
        childrenOf={childrenOf}
        depth={1}
      />
      {editable && <NewDependent taskId={taskId} claim={claim} />}
    </div>
  );
}

/** The fold before an independent claim: its dependents hidden or shown. */
function FoldToggle({
  claim,
  folded,
  onFold,
}: {
  claim: ClaimResponse;
  folded: boolean;
  onFold: ((folded: boolean) => void) | undefined;
}) {
  return (
    <DisclosureToggle
      trigger={{
        "aria-expanded": !folded,
        onClick: () => onFold?.(!folded),
      }}
      open={!folded}
      names={`the dependent claims of claim ${String(claim.number)}`}
    />
  );
}

/** The card as a drag moves it and a position is dropped onto it. */
function useCardDrag(claim: ClaimResponse, index: number) {
  // An independent claim sorts among every independent claim, whatever its category.
  const row = useSortableItem({
    id: claim.claim_id,
    index,
    lane: INDEPENDENTS,
    type: "independent",
    label: `Reorder claim ${String(claim.number)}`,
  });
  const target = useTarget({
    id: ontoClaim(claim.claim_id),
    accept: "position",
    inner: true,
  });
  return { row, target };
}

/**
 * An independent claim and every claim beneath it, as one card: the claim on a
 * tinted band edged in the colour of where it stands, its dependents down a
 * rule beneath it, each with its own beneath it, and a well to add another.
 */
export function ClaimCard({
  taskId,
  editable,
  stage,
  claim,
  index = 0,
  childrenOf,
  folded = false,
  onFold,
}: {
  taskId: string;
  editable: boolean;
  stage: ClaimStrategyResponse;
  claim: ClaimResponse;
  /** Where the claim sits among the independent claims, for the drag that reorders them. */
  index?: number;
  /** The claims directly beneath a claim, in the order the drag is showing. */
  childrenOf: (claimId: string) => ClaimResponse[];
  /** Whether the claims beneath are folded away, and the way to fold or show them. */
  folded?: boolean;
  onFold?: (folded: boolean) => void;
}) {
  const { row, target } = useCardDrag(claim, index);
  const suggestion = useChangesOn(taskId, "claim_strategy")(claim.claim_id);
  const beneath = everyBeneath(claim.claim_id, childrenOf);
  const state = stateOf(claim, beneath);
  // The claim itself confirmed while a dependent is not: the band says so.
  const partly = state === "open" && claim.confirmed;
  return (
    <Panel
      as="li"
      shape="row"
      pad="none"
      ref={editable ? mergeRefs(row.ref, target.ref) : undefined}
      data-testid={`claim-card-${claim.claim_id}`}
      data-state={state}
      className={cn(
        "relative mt-[13px] rounded-[11px] border-l-4",
        CARD[state],
        row.chrome,
        target.chrome,
        suggestion !== null && "ring-2 ring-primary/60",
      )}
    >
      <ClaimRow
        taskId={taskId}
        editable={editable}
        stage={stage}
        claim={claim}
        lead={
          <>
            <FoldToggle claim={claim} folded={folded} onFold={onFold} />
            {editable && row.handle}
          </>
        }
        className={cn(
          "rounded-t-[8px] border-b border-border-subtle py-[9px] pr-3.5 pl-[11px]",
          BAND[state],
          partly && "ring-[1.5px] ring-success/35 ring-inset",
        )}
      />
      <CardBody
        taskId={taskId}
        editable={editable}
        stage={stage}
        claim={claim}
        childrenOf={childrenOf}
        hidden={folded ? beneath.length : null}
        onShow={() => onFold?.(false)}
      />
      {suggestion !== null && <div className="px-3.5 pb-3">{suggestion}</div>}
    </Panel>
  );
}
